import { Price } from '../entity-full';

/**
 * A tradeable object as consumed by the UI, after normalization.
 *
 * The endpoint now outer-joins Price and attaches a SINGULAR `price` object (or
 * null) per row — so a plan with N prices arrives as N duplicate rows. We fold
 * those rows into unique plans with a `prices[]` array via `normalizeCatalog`,
 * so downstream code keeps reading `prices`.
 *
 * Backend quirk: numeric FK/enum columns come back as the literal string "None"
 * when null (e.g. `sub_duration`, `tier`, `tocid`), so consumers must coerce.
 */
export interface CatalogPlan {
  toid: number;
  uuid: string;
  ffid: string;
  name: string;
  name_type: string | 'None';
  description: string | 'None';
  description_json: string[] | 'None' | null;

  totid: number; // tradeable object type (grouping)
  tocid: number | 'None'; // collection
  topid: number | 'None'; // package
  tier: number | 'None';
  order_by: number;
  sub_duration: number | 'None'; // days

  for_user: number;
  for_org: number;
  for_biz: number;
  for_ext_link: number;

  trader_plan: number;
  shopper_plan: number;
  popular: number;
  featured: number;
  atomic: number;
  stackable: number;
  upgradeable: number;
  active: number;
  deleted: number;

  etid: number[] | 'None';
  etid_exclude: number[] | 'None';
  srtid_json: number[] | 'None';
  taxid: number[] | null;
  toid_bundle: number | 'None';

  parameters: Record<string, unknown> | 'None' | null;
  country_iso2: string;
  country_ios2_other: string | 'None';
  date_added: string;
  date_modified: string;

  prices?: Price[]; // folded from the outer-joined `price` rows by normalizeCatalog
}

/**
 * Raw embedded price row from the tradeable_objects outer join. Null columns
 * arrive as the literal string "None" (and `price` itself can be null/"None"
 * when a plan has no price). Only the list-price fields the UI needs are typed.
 */
export interface CatalogPriceRow {
  pricid: number | 'None';
  toid: number | 'None';
  prictid: number | 'None';
  ccode: string;
  country_iso2: string;
  price: number;
  price_old: number | string | null;
  price_min: number | 'None' | null;
  price_max: number | 'None' | null;
  price_per: number | 'None' | null;
  date_added: string;
  date_modified: string;
}

/** A raw catalog row before dedup: one plan + one embedded `price` (or null). */
export interface RawCatalogRow extends Omit<CatalogPlan, 'prices'> {
  price?: CatalogPriceRow | 'None' | null;
}

/** Envelope for `GET /api/admin/tradeable_objects/` (rows not yet deduped). */
export interface CatalogResponse {
  status: number;
  data: { tradeable_objects: RawCatalogRow[] };
}

// ─── Coercion helpers (backend sends "None" for nulls) ─────────────────────────

/** Coerce a possibly-"None" numeric column to a number or null. */
export const asNum = (v: number | 'None' | null | undefined): number | null =>
  v == null || v === 'None' || Number.isNaN(Number(v)) ? null : Number(v);

/** Coerce a possibly-"None" array column to a real array. */
export const asArr = <T>(v: T[] | 'None' | null | undefined): T[] =>
  Array.isArray(v) ? v : [];

/** Map a raw embedded price row into the shared `Price` shape the UI consumes. */
const toPrice = (row: CatalogPriceRow): Price => {
  const old = asNum(row.price_old as number | 'None' | null);
  return {
    pricid: asNum(row.pricid) ?? undefined,
    prictid: asNum(row.prictid) ?? undefined,
    ccode: row.ccode,
    currency: row.ccode,
    country_iso2: row.country_iso2,
    price: Number(row.price),
    price_old: old != null ? String(old) : null,
    date_added: row.date_added,
    date_modified: row.date_modified,
  };
};

/**
 * Fold the outer-joined rows (one row per price, N rows per plan) into unique
 * plans carrying a `prices[]` array, keyed by `toid`. Rows without a real price
 * (`null`/"None") still yield the plan once, with an empty `prices`.
 */
export const normalizeCatalog = (rows: RawCatalogRow[]): CatalogPlan[] => {
  const byToid = new Map<number, CatalogPlan>();
  for (const row of rows) {
    const { price, ...rest } = row;
    let plan = byToid.get(row.toid);
    if (!plan) {
      plan = { ...rest, prices: [] };
      byToid.set(row.toid, plan);
    }
    if (price && price !== 'None' && typeof price === 'object') {
      const mapped = toPrice(price);
      if (!plan.prices!.some((p) => p.pricid === mapped.pricid)) {
        plan.prices!.push(mapped);
      }
    }
  }
  return [...byToid.values()];
};

/** Feature bullet list from `description_json`, tolerating the "None" sentinel. */
export const planFeatures = (plan: CatalogPlan): string[] =>
  Array.isArray(plan.description_json) ? plan.description_json : [];

/** Parameters object, tolerating the "None"/null sentinels. */
export const planParameters = (plan: CatalogPlan): Record<string, unknown> =>
  plan.parameters && plan.parameters !== 'None' && typeof plan.parameters === 'object'
    ? plan.parameters
    : {};

/** The most descriptive label available for a plan. */
export const planTitle = (plan: CatalogPlan): string => {
  const nt = plan.name_type;
  return nt && nt !== 'None' ? nt : plan.name;
};

/** Human-readable duration from `sub_duration` (days). */
export const formatDuration = (plan: CatalogPlan): string => {
  const days = asNum(plan.sub_duration);
  if (days == null) return 'One-time';
  if (days % 365 === 0) {
    const y = days / 365;
    return `${y} year${y === 1 ? '' : 's'}`;
  }
  if (days % 30 === 0) {
    const m = days / 30;
    return `${m} month${m === 1 ? '' : 's'}`;
  }
  if (days % 7 === 0) {
    const w = days / 7;
    return `${w} week${w === 1 ? '' : 's'}`;
  }
  return `${days} day${days === 1 ? '' : 's'}`;
};

/** List price (`prictid` 1) for a plan in the given currency, best-effort. */
export const getPlanListPrice = (plan: CatalogPlan, ccode: string): Price | undefined => {
  const prices = plan.prices ?? [];
  const matches = (p: Price) => (p.ccode ?? p.currency) === ccode;
  return (
    prices.find((p) => (p.prictid ?? 1) === 1 && matches(p)) ??
    prices.find(matches) ??
    prices[0]
  );
};

/** Format a price amount with a currency symbol (₵ for GHS). */
export const formatPriceAmount = (amount: number, ccode: string): string => {
  const symbol = ccode === 'GHS' ? '₵' : `${ccode} `;
  return `${symbol}${Number(amount).toLocaleString()}`;
};

/** Audience chips derived from the `for_*` flags. */
export const planAudiences = (plan: CatalogPlan): string[] => {
  const out: string[] = [];
  if (plan.for_user) out.push('Users');
  if (plan.for_org) out.push('Organizations');
  if (plan.for_biz) out.push('Businesses');
  if (plan.for_ext_link) out.push('External links');
  return out;
};
